import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { ORDER_STATUSES } from '@/services/orderService';
import {
  createNovapostShipment,
  createUkrposhtaShipment,
  downloadNovapostLabel,
  downloadUkrposhtaLabel,
  fetchAdminOrders,
  updateAdminOrderShippingTracking,
  updateAdminOrderStatus,
  type AdminOrderSummary,
} from '@/services/adminService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import type { AdminWorkspaceOrderAction, AdminWorkspaceOrderGroup } from './types';
import type { ManualTrackingFormState } from './ManualTrackingDialog';
import {
  getDefaultConfirmedDateRange,
  isTimestampWithinDateRange,
  matchesSearchTerms,
  normalizeSearchInput,
} from './utils';
import axios from 'axios';

const getApiErrorMessage = (error: unknown, fallback: string): string => {
  if (axios.isAxiosError(error)) {
    const message = error.response?.data?.message;
    if (typeof message === 'string' && message.trim().length > 0) {
      return message;
    }
  }
  if (error instanceof Error && error.message.trim().length > 0) {
    return error.message;
  }
  return fallback;
};

const ORDER_STATUS_FLOW_INDEX = new Map(ORDER_STATUSES.map((status, index) => [status, index]));

const compareOrderStatuses = (left: string, right: string): number => {
  const leftIndex = ORDER_STATUS_FLOW_INDEX.get(left as typeof ORDER_STATUSES[number]);
  const rightIndex = ORDER_STATUS_FLOW_INDEX.get(right as typeof ORDER_STATUSES[number]);

  if (leftIndex != null && rightIndex != null) {
    return leftIndex - rightIndex;
  }

  if (leftIndex != null) {
    return -1;
  }

  if (rightIndex != null) {
    return 1;
  }

  if (left === 'UNKNOWN') {
    return right === 'UNKNOWN' ? 0 : 1;
  }

  if (right === 'UNKNOWN') {
    return -1;
  }

  return left.localeCompare(right);
};

const ADMIN_ORDERS_FILTERS_STORAGE_KEY = 'tulasi.adminOrdersWorkspace.orderFilters';

type StoredAdminOrdersFilters = {
  readonly searchInput: string;
  readonly dateFrom: string;
  readonly dateTo: string;
  readonly visibleStatuses: string[];
};

const readStoredAdminOrdersFilters = (defaults: { readonly dateFrom: string; readonly dateTo: string }): StoredAdminOrdersFilters => {
  if (typeof window === 'undefined') {
    return {
      searchInput: '',
      dateFrom: defaults.dateFrom,
      dateTo: defaults.dateTo,
      visibleStatuses: [],
    };
  }

  try {
    const stored = window.localStorage.getItem(ADMIN_ORDERS_FILTERS_STORAGE_KEY);
    if (!stored) {
      return {
        searchInput: '',
        dateFrom: defaults.dateFrom,
        dateTo: defaults.dateTo,
        visibleStatuses: [],
      };
    }

    const parsed = JSON.parse(stored) as Partial<StoredAdminOrdersFilters>;
    return {
      searchInput: typeof parsed.searchInput === 'string' ? parsed.searchInput : '',
      dateFrom: typeof parsed.dateFrom === 'string' ? parsed.dateFrom : defaults.dateFrom,
      dateTo: typeof parsed.dateTo === 'string' ? parsed.dateTo : defaults.dateTo,
      visibleStatuses: Array.isArray(parsed.visibleStatuses) ? parsed.visibleStatuses.filter((value): value is string => typeof value === 'string') : [],
    };
  } catch {
    return {
      searchInput: '',
      dateFrom: defaults.dateFrom,
      dateTo: defaults.dateTo,
      visibleStatuses: [],
    };
  }
};

export const useAdminOrdersData = (enabled: boolean, onAfterMutation?: () => void) => {
  const { toast } = useToast();
  const navigate = useNavigate();
  const defaultDateRange = useMemo(() => ({ ...getDefaultConfirmedDateRange(), dateTo: '' }), []);
  const storedFilters = useMemo(() => readStoredAdminOrdersFilters(defaultDateRange), [defaultDateRange]);
  const [orders, setOrders] = useState<AdminOrderSummary[]>([]);
  const [status, setStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [visibleStatuses, setVisibleStatuses] = useState<Set<string>>(() => new Set(storedFilters.visibleStatuses));
  const [searchInput, setSearchInput] = useState(storedFilters.searchInput);
  const [dateFrom, setDateFrom] = useState(storedFilters.dateFrom);
  const [dateTo, setDateTo] = useState(storedFilters.dateTo);
  const [debouncedDateFrom, setDebouncedDateFrom] = useState(storedFilters.dateFrom);
  const [debouncedDateTo, setDebouncedDateTo] = useState(storedFilters.dateTo);
  const [selectedUkrposhtaOrder, setSelectedUkrposhtaOrder] = useState<AdminOrderSummary | null>(null);
  const [manualTrackingOrder, setManualTrackingOrder] = useState<AdminOrderSummary | null>(null);
  const [manualTrackingForm, setManualTrackingForm] = useState<ManualTrackingFormState>({
    provider: 'UKRPOSHTA',
    trackingNumber: '',
    shipmentUuid: '',
  });
  const [manualTrackingStatus, setManualTrackingStatus] = useState<'idle' | 'saving' | 'error'>('idle');
  const [manualTrackingError, setManualTrackingError] = useState<string | null>(null);
  const [orderStatusSavingId, setOrderStatusSavingId] = useState<string | null>(null);
  const [trackingOrderId, setTrackingOrderId] = useState<string | null>(null);

  const loadOrders = useCallback(async (dateRange?: { readonly dateFrom: string; readonly dateTo: string }) => {
    const requestDateFrom = dateRange?.dateFrom ?? debouncedDateFrom;
    const requestDateTo = dateRange?.dateTo ?? debouncedDateTo;

    try {
      setStatus('loading');
      setError(null);
      const result = await fetchAdminOrders({ dateFrom: requestDateFrom, dateTo: requestDateTo });
      setOrders([...result]);
      setStatus('idle');
    } catch (err) {
      logging.error('Failed to fetch admin orders', { error: err });
      const message = 'Failed to load orders.';
      setError(message);
      setStatus('error');
      toast({ title: 'Failed to load orders', description: message, variant: 'destructive' });
    }
  }, [debouncedDateFrom, debouncedDateTo, toast]);

  useEffect(() => {
    if (!enabled) {
      return;
    }
    void loadOrders();
  }, [enabled, loadOrders]);

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setDebouncedDateFrom(dateFrom);
      setDebouncedDateTo(dateTo);
    }, 400);

    return () => window.clearTimeout(timeoutId);
  }, [dateFrom, dateTo]);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    window.localStorage.setItem(ADMIN_ORDERS_FILTERS_STORAGE_KEY, JSON.stringify({
      searchInput,
      dateFrom,
      dateTo,
      visibleStatuses: Array.from(visibleStatuses),
    } satisfies StoredAdminOrdersFilters));
  }, [dateFrom, dateTo, searchInput, visibleStatuses]);

  const searchTerms = useMemo(
    () => normalizeSearchInput(searchInput).split(' ').filter((term) => term.length > 0),
    [searchInput],
  );

  const filteredOrders = useMemo(
    () => orders.filter((order) => {
      if (!isTimestampWithinDateRange(order.confirmedAt ?? order.createdAt, dateFrom, dateTo)) {
        return false;
      }

      return matchesSearchTerms(searchTerms, [
        order.displayCode,
        order.id,
        order.customer?.name ?? null,
        order.customer?.email ?? null,
        order.shippingAddress?.firstName ?? null,
        order.shippingAddress?.lastName ?? null,
        order.shippingAddress?.phone ?? null,
        order.shippingAddress?.street1 ?? null,
        order.shippingAddress?.street2 ?? null,
        order.shippingAddress?.city ?? null,
        order.shippingAddress?.region ?? null,
        order.shippingAddress?.postalCode ?? null,
        order.shippingAddress?.countryCode ?? null,
      ]);
    }),
    [dateFrom, dateTo, orders, searchTerms],
  );

  const statusOptions = useMemo(() => {
    const unexpectedStatuses = Array.from(
      new Set(
        orders
          .map((order) => order.status ?? 'UNKNOWN')
          .filter((statusKey) => !ORDER_STATUS_FLOW_INDEX.has(statusKey as typeof ORDER_STATUSES[number])),
      ),
    ).sort(compareOrderStatuses);

    return [...ORDER_STATUSES, ...unexpectedStatuses];
  }, [orders]);

  useEffect(() => {
    setVisibleStatuses((prev) => {
      if (statusOptions.length === 0) {
        return new Set();
      }
      if (prev.size === 0) {
        return new Set(statusOptions);
      }

      const next = new Set(Array.from(prev).filter((statusKey) => statusOptions.some((option) => option === statusKey)));
      if (next.size === 0) {
        return new Set(statusOptions);
      }
      return next;
    });
  }, [statusOptions]);

  const groupedOrders = useMemo<AdminWorkspaceOrderGroup[]>(() => {
    const grouped = new Map<string, AdminOrderSummary[]>();
    for (const order of filteredOrders) {
      const key = order.status ?? 'UNKNOWN';
      if (visibleStatuses.size > 0 && !visibleStatuses.has(key)) {
        continue;
      }
      if (!grouped.has(key)) {
        grouped.set(key, []);
      }
      grouped.get(key)?.push(order);
    }
    return Array.from(grouped.entries())
      .map(([key, groupedItems]) => ({ key, orders: groupedItems }))
      .sort((a, b) => compareOrderStatuses(a.key, b.key));
  }, [filteredOrders, visibleStatuses]);

  const toggleOrderStatusVisibility = useCallback((statusKey: string) => {
    setVisibleStatuses((prev) => {
      const next = new Set(prev);
      if (next.has(statusKey)) {
        next.delete(statusKey);
      } else {
        next.add(statusKey);
      }
      return next;
    });
  }, []);

  const handleOrderGroupPrintLabels = useCallback((statusKey: string, count: number) => {
    analytics.track('button: ui.admin_orders_print_labels', { status: statusKey, count });
    toast({ title: 'Print labels', description: `Queued labels for ${count} order${count === 1 ? '' : 's'}.` });
  }, [toast]);

  const resetFilters = useCallback(() => {
    const nextDateRange = {
      dateFrom: defaultDateRange.dateFrom,
      dateTo: defaultDateRange.dateTo,
    };

    setSearchInput('');
    setDateFrom(nextDateRange.dateFrom);
    setDateTo(nextDateRange.dateTo);
    setDebouncedDateFrom(nextDateRange.dateFrom);
    setDebouncedDateTo(nextDateRange.dateTo);
    setVisibleStatuses(new Set(statusOptions));
    void loadOrders(nextDateRange);
  }, [defaultDateRange.dateFrom, defaultDateRange.dateTo, loadOrders, statusOptions]);

  const openManualTrackingDialog = useCallback((order: AdminOrderSummary) => {
    const provider = order.shippingProvider === 'NOVAPOST' ? 'NOVAPOST' : 'UKRPOSHTA';
    setManualTrackingOrder(order);
    setManualTrackingForm({
      provider,
      trackingNumber: provider === 'NOVAPOST' ? (order.novapostNumber ?? '') : (order.ukrposhtaBarcode ?? ''),
      shipmentUuid: provider === 'NOVAPOST' ? (order.novapostShipmentUuid ?? '') : (order.ukrposhtaShipmentUuid ?? ''),
    });
    setManualTrackingStatus('idle');
    setManualTrackingError(null);
  }, []);

  const closeManualTrackingDialog = useCallback(() => {
    setManualTrackingOrder(null);
    setManualTrackingForm({ provider: 'UKRPOSHTA', trackingNumber: '', shipmentUuid: '' });
    setManualTrackingStatus('idle');
    setManualTrackingError(null);
  }, []);

  const handleManualTrackingSave = useCallback(async () => {
    if (!manualTrackingOrder) {
      return;
    }
    try {
      setManualTrackingStatus('saving');
      setManualTrackingError(null);
      const updatedOrder = await updateAdminOrderShippingTracking(manualTrackingOrder.id, manualTrackingForm);
      setOrders((current) => current.map((order) => order.id === updatedOrder.id ? updatedOrder : order));
      onAfterMutation?.();
      toast({ title: 'Tracking details saved' });
      closeManualTrackingDialog();
    } catch (err) {
      logging.error('Failed to save manual tracking details', { error: err, orderId: manualTrackingOrder.id });
      setManualTrackingError(getApiErrorMessage(err, 'Unable to save the tracking details.'));
      setManualTrackingStatus('error');
    }
  }, [closeManualTrackingDialog, manualTrackingForm, manualTrackingOrder, onAfterMutation, toast]);

  const handleOrderStatusChange = useCallback(async (orderId: string, nextStatus: 'PAID' | 'IN_PRODUCTION' | 'PRINTED' | 'IN_DELIVERY' | 'DELIVERED') => {
    try {
      setOrderStatusSavingId(orderId);
      const updatedOrder = await updateAdminOrderStatus(orderId, nextStatus);
      setOrders((current) => current.map((order) => order.id === updatedOrder.id ? updatedOrder : order));
      await loadOrders();
      onAfterMutation?.();
      toast({ title: `Order moved to ${nextStatus.replace(/_/g, ' ')}` });
    } catch (err) {
      logging.error('Failed to update admin order status', { error: err, orderId, nextStatus });
      toast({
        title: 'Failed to update order status',
        description: getApiErrorMessage(err, 'Unable to update the order status.'),
        variant: 'destructive',
      });
    } finally {
      setOrderStatusSavingId(null);
    }
  }, [loadOrders, onAfterMutation, toast]);

  const handleOrderAction = useCallback(async (action: AdminWorkspaceOrderAction, orderId: string) => {
    analytics.track('dropdown: ui.admin_orders_action', { action, orderId });

    if (action === 'create_ukrposhta') {
      try {
        await createUkrposhtaShipment(orderId);
        toast({ title: 'Ukrposhta shipment created' });
        await loadOrders();
      } catch (err) {
        logging.error('Failed to create Ukrposhta shipment', { error: err, orderId });
        toast({ title: 'Failed to create shipment', variant: 'destructive' });
      }
      return;
    }

    if (action === 'print_ukrposhta') {
      const order = orders.find((candidate) => candidate.id === orderId);
      if (order?.ukrposhtaShipmentUuid) {
        try {
          await downloadUkrposhtaLabel(order.ukrposhtaShipmentUuid);
          toast({ title: 'Label downloaded' });
        } catch (err) {
          logging.error('Failed to download Ukrposhta label', { error: err, orderId });
          toast({ title: 'Failed to download label', description: getApiErrorMessage(err, 'Unable to download the Ukrposhta label right now.'), variant: 'destructive' });
        }
      }
      return;
    }

    if (action === 'show_ukrposhta_summary') {
      const order = orders.find((candidate) => candidate.id === orderId);
      if (order) {
        setSelectedUkrposhtaOrder(order);
      }
      return;
    }

    if (action === 'create_novapost') {
      try {
        const shipment = await createNovapostShipment(orderId);
        await downloadNovapostLabel(shipment.uuid);
        toast({ title: 'NovaPost shipment created', description: `Label downloaded for shipment ${shipment.number}.` });
        await loadOrders();
      } catch (err) {
        logging.error('Failed to create NovaPost shipment', { error: err, orderId });
        toast({ title: 'Failed to create shipment', variant: 'destructive' });
      }
      return;
    }

    if (action === 'set_manual_tracking') {
      const order = orders.find((candidate) => candidate.id === orderId);
      if (order) {
        openManualTrackingDialog(order);
      }
      return;
    }

    if (action === 'track_shipment') {
      const order = orders.find((candidate) => candidate.id === orderId);
      if (order?.shippingProvider === 'UKRPOSHTA' && order.ukrposhtaBarcode) {
        setTrackingOrderId(order.id);
      }
      return;
    }

    if (action === 'print_novapost') {
      const order = orders.find((candidate) => candidate.id === orderId);
      if (order?.novapostShipmentUuid) {
        try {
          await downloadNovapostLabel(order.novapostShipmentUuid);
          toast({ title: 'NovaPost label downloaded' });
        } catch (err) {
          logging.error('Failed to download NovaPost label', { error: err, orderId });
          toast({ title: 'Failed to download label', description: getApiErrorMessage(err, 'Unable to download the NovaPost label right now.'), variant: 'destructive' });
        }
      }
      return;
    }

    if (action === 'process_refund') {
      navigate(`/admin/order-refund?orderId=${orderId}`);
      return;
    }

    toast({ title: action === 'print_label' ? 'Print label' : 'Select pack', description: `Action "${action}" queued for order ${orderId}.` });
  }, [loadOrders, navigate, openManualTrackingDialog, orders, toast]);

  return {
    state: {
      status,
      error,
      orders,
      groupedOrders,
      statusOptions,
      visibleStatuses,
      orderStatusSavingId,
      searchInput,
      dateFrom,
      dateTo,
      selectedUkrposhtaOrder,
      manualTrackingOrder,
      manualTrackingForm,
      manualTrackingStatus,
      manualTrackingError,
      trackingOrderId,
    },
    actions: {
      loadOrders,
      setSearchInput,
      setDateFrom,
      setDateTo,
      toggleOrderStatusVisibility,
      resetFilters,
      handleOrderGroupPrintLabels,
      closeManualTrackingDialog,
      setManualTrackingForm,
      handleManualTrackingSave,
      handleOrderStatusChange,
      handleOrderAction,
      setSelectedUkrposhtaOrder,
      setTrackingOrderId,
    },
  };
};
